import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import LinkedIn from '@mui/icons-material/LinkedIn';
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom';
import moment from 'moment';
import { getAllCandidatsForSociete } from '../../../../services/CandidatService';
import { withSnackbar } from '../../../../utils/SnackbarHOC';
import { getSenioriteFromDate } from '../../../../utils/FunctionHelpers';
import { displaySwipeableDrawerSummaryCandidat } from '../../../../redux/SwipeableDrawer/actions/swipeableDrawerActions';
import { useDispatch } from 'react-redux';
import { commonGridSx, gridLocaleText, makeGridToolbar } from '../../../../utils/gridStyles';
import { DataGrid } from '../../../../components/SortableDataGrid/SortableDataGrid';

const CustomToolbar = makeGridToolbar('Candidats');

const CandidatsSociete = ({ snackbarShowMessage }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [candidatsSociete, setCandidatsSociete] = useState([]);
  const { id } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    setLoading(true);
    getAllCandidatsForSociete(id)
      .then((data) => setCandidatsSociete(data.data.candidats || []))
      .catch(() => snackbarShowMessage('Erreur de chargement des candidats.', 'error', 2000))
      .finally(() => setLoading(false));
  }, [id]);

  const viewCandidat = (candidatId) => {
    const candidatsIds = candidatsSociete.map((c) => c.id);
    history.push({
      pathname: `/candidats/${candidatId}/view`,
      state: { tabIndex: 0, candidatsInList: candidatsIds, candidatIndex: candidatsIds.indexOf(candidatId), numbersTotalOfSelections: candidatsSociete.length },
    });
  };

  const columns = [
    {
      field: 'nom', headerName: 'NOM', flex: 140, minWidth: 140,
      renderCell: (p) => (
        <RouterLink to={`/candidats/${p.row.id}/view`} onClick={(e) => { e.preventDefault(); viewCandidat(p.row.id); }}
          style={{ textDecoration: 'none', fontWeight: 600, color: '#0f172a', fontSize: '1rem', textTransform: 'uppercase' }}>
          {p.value || '—'}
        </RouterLink>
      ),
    },
    {
      field: 'prenom', headerName: 'PRÉNOM', flex: 140, minWidth: 140,
      renderCell: (p) => (
        <RouterLink to={`/candidats/${p.row.id}/view`} onClick={(e) => { e.preventDefault(); viewCandidat(p.row.id); }}
          style={{ textDecoration: 'none', color: '#334155', fontSize: '1rem', textTransform: 'capitalize' }}>
          {p.value || '—'}
        </RouterLink>
      ),
    },
    {
      field: 'fonction', headerName: 'FONCTION', flex: 220, minWidth: 220,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'dateDebutExperience', headerName: 'SÉNIORITÉ', flex: 100, minWidth: 100, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569' }}>
          {p.value ? getSenioriteFromDate(p.value?.date) : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'linkedin', headerName: 'LKD', flex: 60, minWidth: 60, align: 'center', headerAlign: 'center', sortable: false,
      renderCell: (p) => p.value ? (
        <Tooltip title="Voir LinkedIn">
          <a href={p.value} target="_blank" rel="noreferrer" style={{ color: '#0a66c2', lineHeight: 0 }}>
            <LinkedIn sx={{ fontSize: 20 }} />
          </a>
        </Tooltip>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'actions_col', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Voir candidat">
            <IconButton size="small" onClick={() => viewCandidat(p.row.id)}
              sx={{ color: '#03407b', background: '#ebf4fb', borderRadius: '8px', '&:hover': { background: '#d6eaf8' }, p: 0.75 }}>
              <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Voir résumé">
            <IconButton size="small" onClick={() => dispatch(displaySwipeableDrawerSummaryCandidat({ show: true, id: p.row.id }))}
              sx={{ color: '#64748b', p: 0.75, borderRadius: '8px', '&:hover': { background: '#f1f5f9' } }}>
              <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = (candidatsSociete || []).map((c) => ({ ...c }));

  return (
    <Box sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: 'calc(100vh - 300px)', minHeight: 300, background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        loading={loading}
        pageSizeOptions={[50, 100]}
        disableRowSelectionOnClick
        showToolbar
        slots={{ toolbar: CustomToolbar }}
        slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
        localeText={gridLocaleText}
        sx={commonGridSx}
      />
    </Box>
  );
};

export default withSnackbar(CandidatsSociete);

